/**
 * Per-user saved Harry-isms (spin-the-wheel favorites).
 */

import { create } from 'zustand';

export interface HarryIsmEntry {
  id: string;
  text: string;
  pageTitle: string | null;
  ts: number;
}

const FAVORITES_LIMIT = 80;

function storageKey(userId: string): string {
  return `pp_harry_isms_${userId || 'anon'}`;
}

function load(userId: string): HarryIsmEntry[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as HarryIsmEntry[];
  } catch {
    /* ignore */
  }
  return [];
}

function save(userId: string, favorites: HarryIsmEntry[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(favorites));
  } catch {
    /* ignore */
  }
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface HarryIsmState {
  userId: string | null;
  favorites: HarryIsmEntry[];

  hydrate: (userId: string) => void;
  toggleFavorite: (userId: string, entry: { text: string; pageTitle?: string | null }) => void;
  isFavorite: (text: string) => boolean;
  removeFavorite: (userId: string, id: string) => void;
}

export const useHarryIsmStore = create<HarryIsmState>((set, get) => ({
  userId: null,
  favorites: [],

  hydrate: (userId) => {
    if (get().userId === userId) return;
    set({ userId, favorites: load(userId) });
  },

  toggleFavorite: (userId, entry) => {
    const text = entry.text.trim();
    if (!text) return;
    const key = text.toLowerCase();
    const exists = get().favorites.some((f) => f.text.trim().toLowerCase() === key);
    const favorites = exists
      ? get().favorites.filter((f) => f.text.trim().toLowerCase() !== key)
      : [
          {
            id: newId(),
            text,
            pageTitle: entry.pageTitle?.trim() || null,
            ts: Date.now(),
          },
          ...get().favorites,
        ].slice(0, FAVORITES_LIMIT);
    set({ favorites });
    save(userId, favorites);
  },

  isFavorite: (text) => {
    const key = text.trim().toLowerCase();
    return get().favorites.some((f) => f.text.trim().toLowerCase() === key);
  },

  removeFavorite: (userId, id) => {
    const favorites = get().favorites.filter((f) => f.id !== id);
    set({ favorites });
    save(userId, favorites);
  },
}));
